import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, Optional } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatSnackBar } from '@angular/material/snack-bar';
import pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import { ToWords } from 'to-words';
import { catchError, firstValueFrom, of } from 'rxjs';
import { EmailTemplateResolverService } from 'src/app/services/email-template-resolver.service';
import { SalesService } from 'src/app/services/sales.services';
import { TemplateMailComposeDialogComponent } from 'src/app/pages/mail/components/template-mail-compose-dialog/template-mail-compose-dialog.component';
import { MaterialModule } from 'src/app/shared/material.module';

pdfMake.vfs = pdfFonts.vfs;
const toWords = new ToWords();

@Component({
  selector: 'app-sales-invoice-print',
  imports: [CommonModule, MaterialModule, MatProgressBar],
  templateUrl: './sales-invoice-print.component.html',
  styleUrls: ['./sales-invoice-print.component.scss']
})
export class SalesInvoicePrintComponent implements OnInit, OnDestroy {
  billId!: number;
  billData: any;
  previewData: any = null;
  pdfDoc: any;
  pdfSrc: string | null = null;
  isDialogMode = false;
  includeSignature = false;
  includePosAnnexure = false;
  isGenerating = false;
  private generatedBlobUrl: string | null = null;
  private rotatedImageCache = new Map<string, string>();
  private latestGenerationToken = 0;
  private pendingRegeneration = false;
  private readonly monthlyInvoicePageId = 19;
  private readonly sessionData = JSON.parse(sessionStorage.getItem('tokenaccess') || localStorage.getItem('tokenaccess') || '{}');

  constructor(
    private route: ActivatedRoute,
    private salesService: SalesService,
    private emailTemplateResolver: EmailTemplateResolverService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    @Optional() @Inject(MAT_DIALOG_DATA) private dialogData: { id?: number } | null,
    @Optional() private dialogRef: MatDialogRef<SalesInvoicePrintComponent> | null
  ) {}

  ngOnInit(): void {
    document.body.classList.add('invoice-preview-mode');
    this.isDialogMode = !!this.dialogData?.id;
    this.billId = Number(this.dialogData?.id ?? this.route.snapshot.paramMap.get('id'));
    if (!this.billId) {
      return;
    }
    this.loadBillData();
  }

  ngOnDestroy(): void {
    document.body.classList.remove('invoice-preview-mode');
    if (this.generatedBlobUrl) {
      URL.revokeObjectURL(this.generatedBlobUrl);
    }
  }

  loadBillData(): void {
    this.salesService.getMonthlySalesBill_pdf_byid(this.billId).subscribe({
      next: (data: any) => {
        this.billData = data?.[0];
        this.includePosAnnexure = false;
        this.rotatedImageCache.clear();
        void this.generatePdf();
      },
      error: (err) => console.error('Error loading bill', err)
    });
  }

  get hasPosAnnexure(): boolean {
    return !!(
      this.billData &&
      (
        (Array.isArray(this.billData?.pos_item_summary) && this.billData.pos_item_summary.length > 0) ||
        (Array.isArray(this.billData?.pos_card_summary) && this.billData.pos_card_summary.length > 0)
      )
    );
  }

  async generatePdf(): Promise<void> {
    if (!this.billData) {
      return;
    }

    if (this.isGenerating) {
      this.pendingRegeneration = true;
      return;
    }

    this.isGenerating = true;
    this.pendingRegeneration = false;
    this.pdfSrc = null;
    this.previewData = null;
    const generationToken = ++this.latestGenerationToken;

    const b = this.billData;
    const company = b.company || {};
    const client = b.client || {};
    const shipTo = b.shipto || {};
    const bank = b.bank || {};
    const items = Array.isArray(b.items) ? b.items : [];
    const posItemSummary = Array.isArray(b.pos_item_summary) ? b.pos_item_summary : [];
    const posCardSummary = Array.isArray(b.pos_card_summary) ? b.pos_card_summary : [];
    const logo = this.resolveImage([company.logoBase64, company.logobase64, b.logoBase64]);
    const rawSignatureImage = this.resolveImage([
      b.default_signature_image_base64,
      b.signatureBase64,
      b.signaturebase64,
      b.authorizedSignatureBase64,
      b.authorizedsignaturebase64,
      b.billSignatureBase64,
      b.billsignaturebase64,
      company.default_signature_image_base64,
      company.signatureBase64,
      company.signaturebase64,
      company.authorizedSignatureBase64,
      company.authorizedsignaturebase64
    ]);
    const rawStampImage = this.resolveImage([
      b.default_stamp_image_base64,
      b.stampBase64,
      b.stampbase64,
      b.companyStampBase64,
      b.companystampbase64,
      b.billStampBase64,
      b.billstampbase64,
      company.default_stamp_image_base64,
      company.stampBase64,
      company.stampbase64,
      company.companyStampBase64,
      company.companystampbase64
    ]);
    const signSeed = this.billId || Number(b.id || 0);
    const signBlockLayout = this.getSignBlockLayout(signSeed);
    const signatureImage = this.includeSignature && rawSignatureImage
      ? await this.rotateImageDataUrl(rawSignatureImage, signBlockLayout.signatureAngle)
      : null;
    const stampImage = this.includeSignature && rawStampImage
      ? await this.rotateImageDataUrl(rawStampImage, signBlockLayout.stampAngle)
      : null;

    const companyAddress = this.joinAddress([
      company.address1,
      company.address2,
      company.city,
      company.state,
      company.country,
      company.pincode
    ]);
    const clientAddress = this.joinAddress([
      client.address1,
      client.address2,
      client.city,
      client.state,
      client.country,
      client.pincode
    ]);
    const shipToAddress = this.joinAddress([
      shipTo.address1,
      shipTo.address2,
      shipTo.city,
      shipTo.state,
      shipTo.country,
      shipTo.pincode
    ]);

    const billDate = this.formatDateValue(b.billdate);
    const duration = this.joinRange(this.formatDateValue(b.startdate), this.formatDateValue(b.enddate));
    const netAmount = Number(b.netbillamt || b.billamt || 0);
    const amountInWords = `${toWords.convert(netAmount, { currency: true })}/-`;
    const signatoryName = this.includeSignature
      ? this.safeValue(
          b.authorized_signatory_name ||
            b.authorizedSignatoryName ||
            b.authorizedsignatoryname ||
            company.authorized_signatory_name ||
            company.authorizedSignatoryName ||
            company.authorizedsignatoryname ||
            'Authorized Signatory'
        )
      : 'Authorized Signatory';
    const signatoryTitle = this.includeSignature
      ? this.safeValue(
          b.authorized_signatory_title ||
            b.authorizedSignatoryTitle ||
            b.authorizedsignatorytitle ||
            company.authorized_signatory_title ||
            company.authorizedSignatoryTitle ||
            company.authorizedsignatorytitle ||
            '',
          ''
        )
      : '';

    const minimumRows = items.length <= 2 ? 2 : Math.min(4, items.length);
    const itemRows = items.map((item: any, index: number) => ([
      { text: String(index + 1), alignment: 'center' },
      { text: this.safeValue(item.productname), margin: [0, 1, 0, 1] },
      { text: this.safeValue(item.hsnsac), alignment: 'center' },
      { text: this.formatNumber(item.quantity), alignment: 'right' },
      { text: this.formatCurrency(item.productrate, true), alignment: 'right' },
      { text: this.formatCurrency(item.taxableamt, true), alignment: 'right' },
      { text: this.formatCurrency(item.taxamt, true), alignment: 'right' },
      { text: this.safeValue(item.tax), alignment: 'center' },
      { text: this.formatCurrency(item.netamt, true), alignment: 'right', bold: true }
    ]));

    const emptyRowsNeeded = Math.max(0, minimumRows - itemRows.length);
    for (let index = 0; index < emptyRowsNeeded; index++) {
      itemRows.push([
        { text: ' ', fontSize: 9 },
        { text: ' ', fontSize: 9 },
        { text: ' ', fontSize: 9 },
        { text: ' ', fontSize: 9 },
        { text: ' ', fontSize: 9 },
        { text: ' ', fontSize: 9 },
        { text: ' ', fontSize: 9 },
        { text: ' ', fontSize: 9 },
        { text: ' ', fontSize: 9 }
      ]);
    }

    const tableBody = [
      [
        { text: 'SR', style: 'tableHeader' },
        { text: 'DESCRIPTION', style: 'tableHeader' },
        { text: 'HSN/SAC', style: 'tableHeader' },
        { text: 'QTY', style: 'tableHeader' },
        { text: 'RATE', style: 'tableHeader' },
        { text: 'TAXABLE', style: 'tableHeader' },
        { text: 'TAX', style: 'tableHeader' },
        { text: 'TAX TYPE', style: 'tableHeader' },
        { text: 'LINE TOTAL', style: 'tableHeader' }
      ],
      ...itemRows,
      [
        { text: 'GRAND TOTAL', colSpan: 8, alignment: 'right', bold: true, fillColor: '#eef2ff', margin: [0, 3, 8, 3] },
        {}, {}, {}, {}, {}, {}, {},
        { text: this.formatCurrency(netAmount, true), alignment: 'right', bold: true, fillColor: '#eef2ff', margin: [0, 3, 0, 3] }
      ]
    ];

    const summaryBody = [
      [
        { text: 'Taxable Amount', style: 'summaryLabel' },
        { text: this.formatCurrency(b.taxableamt, true), style: 'summaryValue' }
      ],
      [
        { text: 'Tax Amount', style: 'summaryLabel' },
        { text: this.formatCurrency(b.taxamt, true), style: 'summaryValue' }
      ],
      [
        { text: 'Rounded Off', style: 'summaryLabel' },
        { text: this.formatCurrency(b.roundoffamt, true), style: 'summaryValue' }
      ],
      [
        { text: 'Net Amount', style: 'summaryTotalLabel' },
        { text: this.formatCurrency(netAmount, true), style: 'summaryTotalValue' }
      ]
    ];

    this.previewData = {
      logo,
      companyInitials: this.getInitials(company.name),
      companyName: this.safeValue(company.name).toUpperCase(),
      companyNameTitle: this.safeValue(company.name),
      companyAddress,
      companyMeta: this.buildCompanyMeta(company),
      billNo: this.safeValue(b.billno),
      billDate,
      status: this.safeValue(b.status, ''),
      placeOfSupply: this.safeValue(b.placeofsupply, ''),
      clientName: this.safeValue(client.name),
      clientAddress,
      clientMeta: [
        client.email ? `Email: ${client.email}` : '',
        client.mobile ? `Contact: ${client.mobile}` : '',
        client.gstin ? `GSTIN: ${client.gstin}` : ''
      ].filter(Boolean),
      shipToName: `${this.safeValue(shipTo.name)}${shipTo.code ? ` [${shipTo.code}]` : ''}`,
      shipToAddress,
      shipToMeta: [
        shipTo.gstin ? `GSTIN: ${shipTo.gstin}` : '',
        `Bill For: ${this.safeValue(b.billmonth)} ${this.safeValue(b.billyear, '')}`.trim(),
        duration ? `Service Duration: ${duration}` : ''
      ].filter(Boolean),
      items: items.map((item: any, index: number) => ({
        sr: String(index + 1),
        description: this.safeValue(item.productname),
        hsn: this.safeValue(item.hsnsac),
        qty: this.formatNumber(item.quantity),
        rate: this.formatCurrency(item.productrate, true),
        taxable: this.formatCurrency(item.taxableamt, true),
        tax: this.formatCurrency(item.taxamt, true),
        taxType: this.safeValue(item.tax),
        lineTotal: this.formatCurrency(item.netamt, true)
      })),
      netAmount: this.formatCurrency(netAmount, true),
      bankDetails: [
        `Bank: ${this.safeValue(bank.bankname)}`,
        `Branch: ${this.safeValue(bank.branch)}`,
        `A/C No: ${this.safeValue(bank.accountno)}`,
        `IFSC: ${this.safeValue(bank.ifsc)}`
      ],
      amountInWords,
      summary: [
        { label: 'Taxable Amount', value: this.formatCurrency(b.taxableamt, true) },
        { label: 'Tax Amount', value: this.formatCurrency(b.taxamt, true) },
        { label: 'Rounded Off', value: this.formatCurrency(b.roundoffamt, true) },
        { label: 'Net Amount', value: this.formatCurrency(netAmount, true) }
      ],
      stampImage,
      signatureImage,
      signatoryName,
      signatoryTitle,
      posItemSummary: posItemSummary.map((item: any, index: number) => ({
        sr: String(index + 1),
        description: this.safeValue(item.item_name || item.productname),
        hsn: this.safeValue(item.hsnsac),
        qty: this.formatNumber(item.quantity),
        rate: this.formatCurrency(item.productrate, true),
        taxable: this.formatCurrency(item.taxableamount, true),
        tax: this.formatCurrency(item.taxamount, true),
        lineTotal: this.formatCurrency(item.netamount, true),
        orderCount: this.formatNumber(item.order_count || 0),
        taxType: this.safeValue(item.tax, '')
      })),
      posCardSummary: posCardSummary.map((item: any, index: number) => ({
        sr: String(index + 1),
        cardUid: this.safeValue(item.card_uid),
        holderCode: this.safeValue(item.holder_code),
        holderName: this.safeValue(item.holder_name),
        totalOrders: this.formatNumber(item.total_orders),
        netAmount: this.formatCurrency(item.netamount, true)
      }))
    };

    const annexureContent = this.includePosAnnexure
      ? this.buildPosAnnexureContent({
          companyName: this.safeValue(company.name),
          billNo: this.safeValue(b.billno),
          billDate,
          clientName: this.safeValue(client.name),
          duration,
          posItemSummary,
          posCardSummary
        })
      : [];

    const docDefinition: any = {
      pageSize: 'A4',
      pageMargins: [20, 10, 20, 44],
      footer: (currentPage: number, pageCount: number) => ({
        margin: [20, 0, 20, 6],
        stack: [
          {
            canvas: [
              { type: 'line', x1: 0, y1: 0, x2: 555, y2: 0, lineWidth: 1, color: '#8da0b8' },
              { type: 'rect', x: 0, y: 5, w: 132, h: 2.6, color: '#153a6b' },
              { type: 'rect', x: 441, y: 5, w: 114, h: 2.6, color: '#d97706' }
            ]
          },
          {
            margin: [0, 6, 0, 0],
            columns: [
              {
                width: '*',
                text: `Generated using VCS ERP`,
                style: 'footerText'
              },
              {
                width: 90,
                text: `Page ${currentPage} of ${pageCount}`,
                style: 'footerPageText',
                alignment: 'right'
              }
            ]
          }
        ]
      }),
      background: logo
        ? { image: logo, alignment: 'center', width: 300, height: 300, opacity: 0.055, marginTop: 220 }
        : undefined,
      content: [
        {
          canvas: [
            { type: 'rect', x: 0, y: 0, w: 551, h: 6, color: '#153a6b' },
            { type: 'rect', x: 405, y: 0, w: 146, h: 6, color: '#d97706' },
            { type: 'line', x1: 0, y1: 15, x2: 551, y2: 15, lineWidth: 0.7, color: '#e2e8f0' }
          ],
          margin: [0, -10, 0, 4]
        },
        {
          table: {
            widths: [74, '*', 144],
            body: [[
              {
                stack: logo
                  ? [{ image: logo, width: 66, fit: [66, 66], alignment: 'center' }]
                  : [{ text: this.getInitials(company.name), style: 'logoFallback' }],
                border: [false, false, false, false],
                margin: [0, 6, 0, 0]
              },
              {
                stack: [
                  { text: this.safeValue(company.name).toUpperCase(), style: 'companyHeader' },
                  companyAddress ? { text: companyAddress, style: 'companyMeta' } : '',
                  ...this.buildCompanyMeta(company).map((text) => ({ text, style: 'companyMeta' }))
                ].filter(Boolean),
                border: [false, false, false, false],
                margin: [0, 4, 0, 0]
              },
              {
                stack: [
                  { text: 'TAX INVOICE', style: 'invoiceHeaderBadge' },
                  { text: `Invoice No: ${this.safeValue(b.billno)}`, style: 'invoiceMetaStrong' },
                  { text: `Invoice Date: ${billDate}`, style: 'invoiceMeta' },
                  b.status ? { text: `Status: ${this.safeValue(b.status)}`, style: 'invoiceMeta' } : '',
                  b.placeofsupply ? { text: `Place of Supply: ${this.safeValue(b.placeofsupply)}`, style: 'invoiceMeta' } : ''
                ].filter(Boolean),
                fillColor: '#fff8f1',
                margin: [10, 8, 10, 8],
                border: [true, true, true, true]
              }
            ]]
          },
          layout: {
            hLineColor: () => '#d7deea',
            vLineColor: () => '#d7deea'
          }
        },
        {
          columns: [
            {
              stack: [
                { text: 'Bill To', style: 'cardTitle' },
                { text: this.safeValue(client.name), style: 'cardHeading' },
                clientAddress ? { text: clientAddress, style: 'cardText' } : '',
                client.email ? { text: `Email: ${client.email}`, style: 'cardText' } : '',
                client.mobile ? { text: `Contact: ${client.mobile}`, style: 'cardText' } : '',
                client.gstin ? { text: `GSTIN: ${client.gstin}`, style: 'cardText' } : ''
              ].filter(Boolean),
              margin: [8, 8, 8, 8],
              fillColor: '#f8fafc'
            },
            {
              stack: [
                { text: 'Ship To', style: 'cardTitle' },
                { text: `${this.safeValue(shipTo.name)}${shipTo.code ? ` [${shipTo.code}]` : ''}`, style: 'cardHeading' },
                shipToAddress ? { text: shipToAddress, style: 'cardText' } : '',
                shipTo.gstin ? { text: `GSTIN: ${shipTo.gstin}`, style: 'cardText' } : '',
                { text: `Bill For: ${this.safeValue(b.billmonth)} ${this.safeValue(b.billyear, '')}`.trim(), style: 'cardText' },
                duration ? { text: `Service Duration: ${duration}`, style: 'cardText' } : ''
              ].filter(Boolean),
              margin: [8, 8, 8, 8],
              fillColor: '#f8fafc'
            }
          ],
          columnGap: 14,
          margin: [0, 6, 0, 4]
        },
        {
          table: {
            headerRows: 1,
            widths: [20, '*', 40, 28, 48, 54, 45, 50, 54],
            body: tableBody
          },
          layout: {
            fillColor: (rowIndex: number, node: any) => {
              if (rowIndex === 0) {
                return '#0f172a';
              }
              if (rowIndex === node.table.body.length - 1) {
                return '#eef2ff';
              }
              return rowIndex % 2 === 0 ? '#f8fafc' : '#ffffff';
            },
            hLineColor: (rowIndex: number) => rowIndex === 0 ? '#1e293b' : '#93a4bb',
            vLineColor: () => '#a7b5c8',
            hLineWidth: (rowIndex: number, node: any) =>
              rowIndex === 0 || rowIndex === 1 || rowIndex === node.table.body.length ? 1.1 : 0.8,
            vLineWidth: () => 0.8,
            paddingTop: () => 5,
            paddingBottom: () => 5,
            paddingLeft: () => 5,
            paddingRight: () => 5
          }
        },
        {
          unbreakable: true,
          columns: [
            {
              width: '*',
              stack: [
                {
                  stack: [
                    { text: 'Bank Details', style: 'sectionHeader' },
                    { text: `Bank: ${this.safeValue(bank.bankname)}`, style: 'detailText' },
                    { text: `Branch: ${this.safeValue(bank.branch)}`, style: 'detailText' },
                    { text: `A/C No: ${this.safeValue(bank.accountno)}`, style: 'detailText' },
                    { text: `IFSC: ${this.safeValue(bank.ifsc)}`, style: 'detailText' }
                  ],
                  fillColor: '#f8fafc',
                  margin: [7, 7, 7, 7]
                },
                {
                  stack: [
                    { text: 'Amount In Words', style: 'sectionHeader' },
                    { text: amountInWords, style: 'amountWords' }
                  ],
                  fillColor: '#f8fafc',
                  margin: [0, 6, 0, 0]
                }
              ]
            },
            {
              width: 188,
              stack: [
                {
                  table: {
                    widths: ['*', 'auto'],
                    body: summaryBody
                  },
                  layout: {
                    hLineColor: () => '#93a4bb',
                    vLineColor: () => '#a7b5c8',
                    hLineWidth: () => 0.9,
                    vLineWidth: () => 0.9,
                    paddingTop: () => 5,
                    paddingBottom: () => 5,
                    paddingLeft: () => 6,
                    paddingRight: () => 6
                  }
                },
                {
                  stack: [
                    {
                      text: `For ${this.safeValue(company.name)}`,
                      style: 'signatoryCompanyInline',
                      alignment: 'right',
                      margin: [0, 0, 0, 4]
                    },
                    {
                      columns: [
                        {
                          width: '*',
                          text: ''
                        },
                        {
                          width: 95,
                          text: ''
                        },
                        {
                          width: 56,
                          stack: [
                            stampImage
                              ? { image: stampImage, fit: signBlockLayout.stampFit, opacity: signBlockLayout.stampOpacity, alignment: 'right', margin: signBlockLayout.stampMargin }
                              : { text: ' ', margin: [0, 0, 0, 20] }
                          ]
                        },
                        {
                          width: 46,
                          stack: [
                            signatureImage
                              ? { image: signatureImage, fit: signBlockLayout.signatureFit, alignment: 'right', margin: signBlockLayout.signatureMargin }
                              : { text: ' ', margin: [0, 4, 0, 20] }
                          ]
                        }
                      ],
                      columnGap: 0,
                      margin: [0, 0, 0, 0]
                    },
                    {
                      text: this.includeSignature
                        ? this.safeValue(
                            b.authorized_signatory_name ||
                              b.authorizedSignatoryName ||
                              b.authorizedsignatoryname ||
                              company.authorized_signatory_name ||
                              company.authorizedSignatoryName ||
                              company.authorizedsignatoryname ||
                              'Authorized Signatory'
                          )
                        : 'Authorized Signatory',
                      style: 'signatoryName',
                      alignment: 'right',
                      margin: [0, this.includeSignature ? 0 : 12, 0, 0]
                    },
                    this.includeSignature &&
                    this.safeValue(
                      b.authorized_signatory_title ||
                        b.authorizedSignatoryTitle ||
                        b.authorizedsignatorytitle ||
                        company.authorized_signatory_title ||
                        company.authorizedSignatoryTitle ||
                        company.authorizedsignatorytitle ||
                        '',
                      ''
                    )
                      ? {
                          text: this.safeValue(
                            b.authorized_signatory_title ||
                              b.authorizedSignatoryTitle ||
                              b.authorizedsignatorytitle ||
                              company.authorized_signatory_title ||
                              company.authorizedSignatoryTitle ||
                              company.authorizedsignatorytitle ||
                              '',
                            ''
                          ),
                          style: 'signatoryRole',
                          alignment: 'right'
                        }
                      : ''
                  ],
                  margin: [0, 8, 0, 0]
                }
              ]
            }
          ],
          columnGap: 12,
          margin: [0, 4, 0, 0]
        },
        ...annexureContent
      ],
      styles: {
        logoFallback: { fontSize: 22, bold: true, color: '#0f172a', alignment: 'center', margin: [0, 18, 0, 0] },
        companyHeader: { fontSize: 17, bold: true, color: '#11284c', alignment: 'center' },
        companyMeta: { fontSize: 8.3, color: '#3f526d', margin: [0, 1, 0, 0], alignment: 'center' },
        invoiceHeaderBadge: { fontSize: 14, bold: true, alignment: 'center', color: '#11284c', margin: [0, 0, 0, 8] },
        invoiceMetaStrong: { fontSize: 9.8, bold: true, color: '#11284c', margin: [0, 2, 0, 0] },
        invoiceMeta: { fontSize: 8.8, color: '#43546b', margin: [0, 2, 0, 0] },
        cardTitle: { fontSize: 8.2, bold: true, color: '#1d4ed8', margin: [0, 0, 0, 4] },
        cardHeading: { fontSize: 10.8, bold: true, color: '#11284c', margin: [0, 0, 0, 3] },
        cardText: { fontSize: 8.7, color: '#24364e', margin: [0, 1, 0, 0] },
        sectionHeader: { fontSize: 9, bold: true, color: '#11284c', margin: [0, 0, 0, 4] },
        tableHeader: { bold: true, fontSize: 7.8, color: '#ffffff', alignment: 'center' },
        annexureTitle: { fontSize: 16, bold: true, color: '#11284c', margin: [0, 0, 0, 8] },
        annexureSectionTitle: { fontSize: 11.2, bold: true, color: '#1d4ed8', margin: [0, 0, 0, 6] },
        detailText: { fontSize: 8.6, color: '#24364e', margin: [0, 1, 0, 0] },
        amountWords: { fontSize: 9.1, color: '#11284c', bold: true },
        summaryLabel: { fontSize: 8.5, color: '#43546b', alignment: 'right' },
        summaryValue: { fontSize: 8.8, color: '#11284c', alignment: 'right', bold: true },
        summaryTotalLabel: { fontSize: 9.2, color: '#11284c', alignment: 'right', bold: true },
        summaryTotalValue: { fontSize: 11.6, color: '#c2410c', alignment: 'right', bold: true },
        signatoryCompany: { fontSize: 9.2, bold: true, color: '#11284c' },
        signatoryCompanyInline: { fontSize: 8.3, bold: true, color: '#334155' },
        signatoryLabel: { fontSize: 8.1, color: '#334155' },
        signatoryName: { fontSize: 9.2, bold: true, color: '#11284c', margin: [0, 0, 0, 0] },
        signatoryRole: { fontSize: 8.5, color: '#475569', margin: [0, 1, 0, 0] },
        footerText: { fontSize: 6.8, color: '#64748b', margin: [0, 0, 0, 0] },
        footerTextRight: { fontSize: 6.8, color: '#64748b', alignment: 'right' },
        footerPageText: { fontSize: 6.8, color: '#64748b' }
      },
      defaultStyle: {
        fontSize: 9,
        color: '#0f172a'
      }
    };

    this.pdfDoc = pdfMake.createPdf(docDefinition);
    this.pdfDoc.getBlob((blob: Blob) => {
      const newBlobUrl = URL.createObjectURL(blob);

      if (generationToken !== this.latestGenerationToken) {
        URL.revokeObjectURL(newBlobUrl);
        return;
      }

      if (this.generatedBlobUrl) {
        URL.revokeObjectURL(this.generatedBlobUrl);
      }

      this.generatedBlobUrl = newBlobUrl;
      this.pdfSrc = `${this.generatedBlobUrl}#toolbar=0&navpanes=0&scrollbar=1&zoom=100`;
      this.isGenerating = false;

      if (this.pendingRegeneration) {
        void this.generatePdf();
      }
    });
  }

  onSignatureToggle(checked: boolean) {
    if (this.includeSignature === checked) {
      return;
    }
    this.includeSignature = checked;
    void this.generatePdf();
  }

  onPosAnnexureToggle(checked: boolean) {
    if (this.includePosAnnexure === checked) {
      return;
    }
    this.includePosAnnexure = checked;
    void this.generatePdf();
  }

  printPdf() {
    this.pdfDoc?.print();
  }

  downloadPdf() {
    this.pdfDoc?.download(`Invoice-${this.billData?.billno || this.billId}.pdf`);
  }

  async sendMail() {
    if (!this.billData || !this.previewData || !this.pdfDoc) {
      this.snackBar.open('Invoice preview is still loading. Please try again in a moment.', 'Close', { duration: 3000 });
      return;
    }

    if (this.isGenerating) {
      this.snackBar.open('PDF generation is in progress. Please wait a moment.', 'Close', { duration: 3000 });
      return;
    }

    try {
      const resolved = await firstValueFrom(
        this.emailTemplateResolver.resolveMonthlySalesMailDraft(this.monthlyInvoicePageId, this.billId)
      );
      const attachment = await this.buildInvoiceAttachment();
      const draft = {
        ...resolved.draft,
        companyid: Number(this.sessionData?.companyId || 0),
        userid: Number(this.decodeSessionUserId() || 0),
        files: [...(resolved.draft.files || []), attachment]
      };
      this.dialog.open(TemplateMailComposeDialogComponent, {
        width: 'auto',
        maxWidth: 'calc(100vw - 12px)',
        panelClass: 'mail-compose-dialog-panel',
        autoFocus: false,
        data: {
          account: null,
          draft
        }
      });
    } catch (error) {
      console.error('Failed to prepare invoice mail', error);
      this.snackBar.open('Unable to prepare invoice email draft right now.', 'Close', { duration: 3500 });
    }
  }

  closePreview() {
    this.dialogRef?.close();
  }

  private buildCompanyMeta(company: any): string[] {
    return [
      this.metaLine('GSTIN', company.gstin),
      this.metaLine('PAN', company.pan),
      this.metaLine('MSME', company.msmeNo),
      this.metaLine('Email', company.email),
      this.metaLine('Website', company.website)
    ].filter(Boolean) as string[];
  }

  private metaLine(label: string, value: any): string {
    const normalized = this.normalizePart(value);
    if (!normalized || ['-', 'n/a', 'na', 'null', 'undefined'].includes(normalized.toLowerCase())) {
      return '';
    }

    return `${label}: ${normalized}`;
  }

  private resolveImage(candidates: any[]): string | null {
    for (const candidate of candidates) {
      const normalized = this.normalizeImageCandidate(candidate);
      if (normalized) {
        return normalized;
      }
    }

    return null;
  }

  private normalizeImageCandidate(value: any): string | null {
    if (typeof value !== 'string') {
      return null;
    }

    const image = value.trim();
    if (!image) {
      return null;
    }

    if (image.startsWith('data:image')) {
      return image;
    }

    // Some APIs return pure base64 without the data URL prefix.
    if (/^[A-Za-z0-9+/=\r\n]+$/.test(image) && image.length > 120) {
      return `data:image/png;base64,${image.replace(/\s+/g, '')}`;
    }

    return null;
  }

  private getSignBlockLayout(seedBase: number) {
    const seed = Math.abs(seedBase || 1) * 97;
    const next = (min: number, max: number, step = 1, salt = 0) =>
      this.seededValue(seed + salt, min, max, step);

    return {
      stampFit: [next(62, 68, 1, 11), next(62, 68, 1, 17)] as [number, number],
      stampMargin: [next(0, 4, 1, 3), next(-2, 3, 1, 29), next(-2, 2, 1, 31), next(-5, -1, 1, 37)] as [number, number, number, number],
      stampOpacity: 0.74,
      stampAngle: next(-4, 4, 1, 41),
      signatureFit: [next(106, 116, 1, 43), next(34, 40, 1, 47)] as [number, number],
      signatureMargin: [next(2, 8, 1, 53), next(8, 14, 1, 59), 0, next(-2, 3, 1, 61)] as [number, number, number, number],
      signatureAngle: next(-3, 3, 1, 67)
    };
  }

  private seededValue(seed: number, min: number, max: number, step = 1): number {
    const spread = Math.floor((max - min) / step) + 1;
    const raw = Math.abs(Math.sin(seed) * 10000);
    const offset = Math.floor(raw % spread);
    return min + offset * step;
  }

  private rotateImageDataUrl(dataUrl: string, angle: number): Promise<string> {
    return new Promise((resolve) => {
      const cacheKey = `${angle}::${dataUrl.slice(0, 120)}`;
      const cached = this.rotatedImageCache.get(cacheKey);
      if (cached) {
        resolve(cached);
        return;
      }

      if (!angle) {
        resolve(dataUrl);
        return;
      }

      const image = new Image();
      image.onload = () => {
        const radians = (angle * Math.PI) / 180;
        const sin = Math.abs(Math.sin(radians));
        const cos = Math.abs(Math.cos(radians));
        const width = image.width;
        const height = image.height;
        const rotatedWidth = Math.ceil(width * cos + height * sin);
        const rotatedHeight = Math.ceil(width * sin + height * cos);

        const canvas = document.createElement('canvas');
        canvas.width = rotatedWidth;
        canvas.height = rotatedHeight;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve(dataUrl);
          return;
        }

        ctx.clearRect(0, 0, rotatedWidth, rotatedHeight);
        ctx.translate(rotatedWidth / 2, rotatedHeight / 2);
        ctx.rotate(radians);
        ctx.drawImage(image, -width / 2, -height / 2, width, height);
        const rotated = canvas.toDataURL('image/png');
        this.rotatedImageCache.set(cacheKey, rotated);
        resolve(rotated);
      };

      image.onerror = () => resolve(dataUrl);
      image.src = dataUrl;
    });
  }

  private safeValue(value: any, fallback = '-'): string {
    if (value === null || value === undefined || value === '') {
      return fallback;
    }
    return String(value);
  }

  private buildPosAnnexureContent(input: {
    companyName: string;
    billNo: string;
    billDate: string;
    clientName: string;
    duration: string;
    posItemSummary: any[];
    posCardSummary: any[];
  }): any[] {
    if (!input.posItemSummary.length && !input.posCardSummary.length) {
      return [];
    }

    const cardBody = [
      [
        { text: 'SR', style: 'tableHeader' },
        { text: 'CARD UID', style: 'tableHeader' },
        { text: 'HOLDER CODE', style: 'tableHeader' },
        { text: 'HOLDER NAME', style: 'tableHeader' },
        { text: 'ORDERS', style: 'tableHeader' },
        { text: 'NET AMOUNT', style: 'tableHeader' }
      ],
      ...input.posCardSummary.map((item: any, index: number) => ([
        { text: String(index + 1), alignment: 'center' },
        { text: this.safeValue(item.card_uid) },
        { text: this.safeValue(item.holder_code, ''), alignment: 'center' },
        { text: this.safeValue(item.holder_name) },
        { text: this.formatNumber(item.total_orders), alignment: 'right' },
        { text: this.formatCurrency(item.netamount, true), alignment: 'right', bold: true }
      ]))
    ];

    return [
      {
        pageBreak: 'before',
        stack: [
          { text: 'POS Postpaid Summary Annexure', style: 'annexureTitle' },
          {
            columns: [
              {
                width: '*',
                stack: [
                  { text: `Invoice No: ${input.billNo}`, style: 'invoiceMetaStrong' },
                  { text: `Invoice Date: ${input.billDate}`, style: 'invoiceMeta' },
                  { text: `Client: ${input.clientName}`, style: 'invoiceMeta' }
                ]
              },
              {
                width: 220,
                stack: [
                  { text: `Company: ${input.companyName}`, style: 'invoiceMetaStrong', alignment: 'right' },
                  input.duration ? { text: `Service Duration: ${input.duration}`, style: 'invoiceMeta', alignment: 'right' } : ''
                ].filter(Boolean)
              }
            ],
            margin: [0, 0, 0, 10]
          },
          input.posCardSummary.length
            ? {
                stack: [
                  { text: 'Card-wise POS Summary', style: 'annexureSectionTitle' },
                  {
                    table: {
                      headerRows: 1,
                      widths: [20, 94, 72, '*', 52, 78],
                      body: cardBody
                    },
                    layout: {
                      fillColor: (rowIndex: number) => rowIndex === 0 ? '#0f172a' : rowIndex % 2 === 0 ? '#f8fafc' : '#ffffff',
                      hLineColor: () => '#93a4bb',
                      vLineColor: () => '#a7b5c8',
                      hLineWidth: () => 0.8,
                      vLineWidth: () => 0.8,
                      paddingTop: () => 5,
                      paddingBottom: () => 5,
                      paddingLeft: () => 5,
                      paddingRight: () => 5
                    }
                  }
                ]
              }
            : ''
        ].filter(Boolean)
      }
    ];
  }

  private joinAddress(parts: any[]): string {
    return parts
      .map((part) => this.normalizePart(part))
      .filter(Boolean)
      .join(', ');
  }

  private joinRange(start: string, end: string): string {
    return [start, end].filter((value) => value && value !== '-').join(' to ');
  }

  private normalizePart(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }
    const text = String(value).trim();
    return text === '-' ? '' : text;
  }

  private formatCurrency(value: any, withSymbol = false): string {
    const amount = Number(value || 0);
    if (Number.isNaN(amount)) {
      return this.safeValue(value);
    }

    const formatted = new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);

    return withSymbol ? `₹ ${formatted}` : formatted;
  }

  private formatNumber(value: any): string {
    const amount = Number(value || 0);
    if (Number.isNaN(amount)) {
      return this.safeValue(value);
    }

    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(amount);
  }

  private formatDateValue(value: any): string {
    if (!value) {
      return '-';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return this.safeValue(value);
    }

    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }).format(date);
  }

  private getInitials(value: any): string {
    const text = this.safeValue(value, 'VCS');
    return text
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part.charAt(0))
      .join('')
      .toUpperCase();
  }

  private async buildInvoiceAttachment(): Promise<File> {
    const blob = await new Promise<Blob>((resolve, reject) => {
      if (!this.pdfDoc) {
        reject(new Error('PDF document is not ready.'));
        return;
      }

      this.pdfDoc.getBlob((pdfBlob: Blob) => resolve(pdfBlob));
    });

    const invoiceNo = this.safeValue(this.billData?.billno, String(this.billId)).replace(/[^\w.-]+/g, '-');
    return new File([blob], `Monthly-Invoice-${invoiceNo}.pdf`, { type: 'application/pdf' });
  }

  private decodeSessionUserId(): number {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token') || '';
    try {
      const payload = JSON.parse(atob(token.split('.')[1] || ''));
      return Number(payload?.unique_name || payload?.nameid || this.sessionData?.userId || this.sessionData?.userid || 0);
    } catch {
      return Number(this.sessionData?.userId || this.sessionData?.userid || 0);
    }
  }
}
